import { Button } from "@/components/ui/button";
import { Star, ArrowUpRight } from "lucide-react";
import heroDesktop from "@/assets/rio-hero-breathtaking.jpg?w=1920&quality=72&format=webp";
import heroMobile from "@/assets/rio-hero-breathtaking.jpg?w=900&quality=70&format=webp";
import heroFallback from "@/assets/rio-hero-breathtaking.jpg?w=1600&quality=75&format=jpg";
import { useLanguage } from "@/contexts/LanguageContext";

const Hero = () => {
  const { t } = useLanguage();

  const scrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Responsive hero image */}
      <picture className="absolute inset-0 -z-10">
        <source media="(max-width: 768px)" srcSet={heroMobile} type="image/webp" />
        <source srcSet={heroDesktop} type="image/webp" />
        <img
          src={heroFallback}
          alt="Aerial view of Rio de Janeiro coastline"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </picture>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] -z-10" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center py-20 md:py-0">
        <div className="max-w-5xl mx-auto">
          {/* Compact credential badge (mobile) / full strip (desktop) */}
          <div className="bg-white/95 backdrop-blur-sm rounded-full md:rounded-lg px-4 md:px-5 py-2 md:py-3 mb-5 md:mb-8 inline-block border border-white/20 max-w-3xl">
            <p className="text-xs md:text-sm font-medium text-slate-700 leading-relaxed">
              <span className="md:hidden">Board-Certified · Adults &amp; Pediatrics · CFM Compliant</span>
              <span className="hidden md:inline">
                Adults &amp; Pediatrics · 5+ years experience · Telemedicine CFM-compliant ·{" "}
                Online consultations Brazil-wide · Same-day video visits
              </span>
            </p>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight max-w-4xl mx-auto drop-shadow-lg">
            {t('hero.headline')}
          </h1>

          <p className="text-base md:text-xl text-white/90 mb-7 md:mb-10 font-medium max-w-3xl mx-auto leading-relaxed drop-shadow-md">
            <span className="md:hidden">
              Fast, confidential consultations in English for travelers and expats anywhere in Brazil.
            </span>
            <span className="hidden md:inline">{t('hero.subheadline')}</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-5 md:mb-6">
            <Button size="lg" className="w-full sm:w-auto sm:min-w-[280px] h-14 text-base font-semibold" onClick={scrollToPricing}>
              {t('hero.cta1')}
            </Button>
            <Button variant="outline" size="lg" className="w-full sm:w-auto sm:min-w-[280px] h-12 md:h-14 text-sm md:text-base font-semibold bg-white/10 border-2 border-white/30 text-white hover:bg-white hover:text-primary backdrop-blur-sm" onClick={scrollToPricing}>
              {t('hero.cta2')}
            </Button>
          </div>

          <div className="mb-4">
            <a
              href="https://jitnikovdossantos.doctorsa.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/95 border border-white/20 rounded-full px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-semibold text-slate-800 hover:bg-white transition-colors shadow-lg"
            >
              <span className="flex text-amber-500" aria-hidden="true">
                <Star className="h-3.5 w-3.5 md:h-4 md:w-4 fill-current" />
                <Star className="h-3.5 w-3.5 md:h-4 md:w-4 fill-current" />
                <Star className="h-3.5 w-3.5 md:h-4 md:w-4 fill-current" />
                <Star className="h-3.5 w-3.5 md:h-4 md:w-4 fill-current" />
                <Star className="h-3.5 w-3.5 md:h-4 md:w-4 fill-current" />
              </span>
              <span className="md:hidden">Rated 5.0 — See all reviews</span>
              <span className="hidden md:inline">Rated 5.0 by international patients — See all reviews</span>
              <ArrowUpRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </a>
          </div>

          <p className="text-xs md:text-sm text-white drop-shadow-sm mb-2 max-w-[280px] sm:max-w-none mx-auto">
            {t('hero.microcopy')}
          </p>
          <p className="hidden md:block text-sm text-white max-w-2xl mx-auto drop-shadow-sm">
            Prescriptions issued only when clinically indicated after live medical evaluation. We do not sell or deliver medications.
          </p>
          <p className="md:hidden text-[11px] text-white/85 max-w-[260px] mx-auto drop-shadow-sm">
            Prescriptions only after live medical evaluation.
          </p>
        </div>
      </div>

    </section>
  );
};

export default Hero;
